import { Tooltip } from "../../../../../../../../../../components/Tooltip/Tooltip";
import SingleDatePicker from "../../../../../../../../../../components/SingleDatePicker/SingleDatePicker";
import TimePicker from "../../../../../../../../../../components/TimePicker/TimePicker";
import Dropdown from "../../../../../../../../../../components/Dropdown/Dropdown";
import CalendarIcon from "../../../../../../../../../../assets/svg/calendar-icon";
import ClockIcon from "../../../../../../../../../../assets/svg/clock-icon";
import ExternalLinkIcon from "../../../../../../../../../../assets/svg/external-link-icon";
import InfoBtnIcon from "../../../../../../../../../../assets/svg/info-btn-icon";
import {
  FIELD_LABEL_URL_VISIBILITY,
  FIELD_LABEL_FILE_URL,
  FIELD_LABEL_EXPIRATION_DATE,
  FIELD_TOOLTIP_EXPIRATION_DATE,
  LINK_OPEN_NEW_WINDOW,
  LINK_COPY_URL,
  FILE_URL_NO_URL_HINT,
  PLACEHOLDER_DATE,
  PLACEHOLDER_TIME,
  PLACEHOLDER_EMPTY,
  VISIBILITY_OPTIONS,
} from "../../../constants/fileManagement.constants";
import {
  parseExpirationDateTime,
  getEarliestQuarterHourFromNow,
  copyToClipboardWithToast,
  openUrlInNewWindow,
} from "../../../utils/fileManagement.utils";
import type { FileDetailsAccessAndUrlsProps } from "../../../types/fileManagement.types";

const FileDetailsAccessAndUrls = ({
  visibility,
  shortUrl,
  fileExpirationTime,
  onVisibilityChange,
  onExpirationDateChange,
  onExpirationTimeChange,
}: FileDetailsAccessAndUrlsProps) => {
  const { pickerDate: expirationDate, pickerTime: expirationTime } =
    parseExpirationDateTime(fileExpirationTime);
  const earliestAllowedTime = getEarliestQuarterHourFromNow();
  const isExpirationDateToday =
    expirationDate?.toDateString() === new Date().toDateString();

  const visibilityOptions = VISIBILITY_OPTIONS.map((option) => {
    const isSelected = option.value === visibility;
    return {
      value: option.value,
      selectedOptionContent: option.label,
      content: (
        <div className="flex flex-col gap-0.5">
          <span
            className={`text-md text-verba-indigo dark:text-verba-indigo-dk ${
              isSelected ? "font-semibold" : "font-medium"
            }`}
          >
            {option.label}
          </span>
          <span className="text-xs font-normal text-analytics-gray dark:text-analytics-gray-dk leading-normal">
            {option.description}
          </span>
        </div>
      ),
    };
  });

  const handleOpenInNewWindow = () => {
    if (!shortUrl) return;
    openUrlInNewWindow(shortUrl);
  };

  const handleCopyUrl = async () => {
    if (!shortUrl) return;
    await copyToClipboardWithToast(shortUrl);
  };

  return (
    <div className="flex flex-col gap-4 pb-[30px] pt-2.5">
      <div className="flex flex-col gap-1.5 w-[340px]">
        <Dropdown
          options={visibilityOptions}
          value={visibility}
          onChange={onVisibilityChange}
          label={FIELD_LABEL_URL_VISIBILITY}
          labelClassName="text-md font-medium"
          triggerClassName="rounded-lg bg-pure-white dark:bg-pure-white-dk !py-0.5"
          selectedOptionTextClassName="text-md font-medium text-verba-blue dark:text-verba-blue-dk"
          optionClassName="!px-3 !py-2.5 border-b border-border-gray dark:border-border-gray-dk"
          checkIconClassName="!w-[18px] !h-[18px]"
          ariaLabel="Select URL visibility"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
          {FIELD_LABEL_FILE_URL}
        </span>
        <div className="flex items-center h-9 px-2 border border-border-gray dark:border-border-gray-dk rounded-lg bg-pure-white dark:bg-pure-white-dk">
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
            {shortUrl || PLACEHOLDER_EMPTY}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-3 h-[21px]">
            <button
              type="button"
              disabled={!shortUrl}
              onClick={handleOpenInNewWindow}
              className={`flex items-center gap-1 text-md font-medium ${
                shortUrl
                  ? "text-verba-blue dark:text-verba-blue-dk hover:underline cursor-pointer"
                  : "text-gray-placeholder dark:text-gray-placeholder-dk cursor-not-allowed opacity-50"
              }`}
            >
              <ExternalLinkIcon
                className={`w-4 h-4 shrink-0 ${
                  shortUrl
                    ? "text-verba-blue dark:text-verba-blue-dk"
                    : "text-gray-placeholder dark:text-gray-placeholder-dk"
                }`}
              />
              {LINK_OPEN_NEW_WINDOW}
            </button>
            <span className="text-md font-medium text-border-gray dark:text-border-gray-dk">
              |
            </span>
            <button
              type="button"
              disabled={!shortUrl}
              onClick={handleCopyUrl}
              className={`text-md font-medium ${
                shortUrl
                  ? "text-verba-blue dark:text-verba-blue-dk hover:underline cursor-pointer"
                  : "text-gray-placeholder dark:text-gray-placeholder-dk cursor-not-allowed opacity-50"
              }`}
            >
              {LINK_COPY_URL}
            </button>
          </div>
          {!shortUrl && (
            <span className="text-xs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
              {FILE_URL_NO_URL_HINT}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center">
          <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
            {FIELD_LABEL_EXPIRATION_DATE}
          </span>
          <Tooltip
            content={FIELD_TOOLTIP_EXPIRATION_DATE}
            side="top"
            align="center"
            contentClassName="text-md text-center w-[220px]"
          >
            <button
              type="button"
              aria-label="File expiration date information"
              className="inline-flex shrink-0 p-0 border-0 bg-transparent cursor-pointer text-gray-placeholder dark:text-gray-placeholder-dk"
            >
              <InfoBtnIcon className="w-[18px] h-[18px] shrink-0" />
            </button>
          </Tooltip>
        </div>
        <div className="flex gap-1.5 w-[264px]">
          <SingleDatePicker
            value={expirationDate}
            onChange={onExpirationDateChange}
            Icon={CalendarIcon}
            disablePastDates
            placeholder={PLACEHOLDER_DATE}
            containerClassName="flex-1"
            inputClassName="h-9 px-2 border border-border-gray dark:border-border-gray-dk rounded-lg bg-pure-white dark:bg-pure-white-dk text-md font-normal text-heading-text dark:text-heading-text-dk"
            iconClassName="w-[15px] h-[15px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0"
            calendarClassName="!text-[11px]"
            calendarContainerClassName="w-max h-max !rounded-lg"
          />
          <TimePicker
            value={expirationTime ?? ""}
            onChange={onExpirationTimeChange}
            placeholder={PLACEHOLDER_TIME}
            Icon={ClockIcon}
            showChevron={false}
            disabled={!expirationDate}
            minTime={isExpirationDateToday ? earliestAllowedTime : undefined}
            iconClassName="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk shrink-0"
            className="flex-1"
            triggerClassName="!border-border-gray dark:!border-border-gray-dk !rounded-lg bg-pure-white dark:bg-pure-white-dk"
            selectedOptionContainerClassName="!w-[129px] !py-[8.5px] !gap-1.5 !px-2 !border-none"
            placeholderClassName="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk"
            optionClassName="text-md font-normal text-heading-text dark:text-heading-text-dk border-none"
            dropdownClassName="bottom-full mb-1 !mt-0"
          />
        </div>
      </div>
    </div>
  );
};

export default FileDetailsAccessAndUrls;
